import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {Button, Select} from 'antd';
import {useTranslation} from 'react-i18next';
import {CloseOutlined} from '@ant-design/icons';
import {
  StyledUserMultiSelectPlaceholder,
  StyledUserMultiSelectWrapper,
  StyledUserMultiSelectSelectedList,
  StyledUserMultiSelectSelectedItem,
  StyledUserMultiSelectSelectedItemLabel,
} from './StyledUserMultiSelect';
import {formHelpers} from '../../../utils/helpers';
import {colorsStyles} from '../../../utils/styles';

const UserMultiSelect = ({
  value,
  onChange,
  disabled,
  size = 'large',
  valueKey = 'email',
  optionFilterProp = 'optionLabel',
  allowClear = true,
  showSelectedList = true,
  className,
  style,
  selectProps = {},
  excludeEmployeeEmail = false,
  employeeIdKey = 'email',
  disableBackspaceRemove = true,
}) => {
  const [t] = useTranslation('main');

  const {employees, employeeEmail} = useSelector(state => ({
    employees: state?.company?.employees || [],
    employeeEmail: state?.user?.employee?.email,
  }));

  const employeeOptions = useMemo(() => {
    return formHelpers.getEmployeeOptions({
      employees, employeeEmail, t, valueKey, excludeEmployeeEmail, idKey: employeeIdKey
    });
  }, [employees, employeeEmail, excludeEmployeeEmail, employeeIdKey, t, valueKey]);

  const selectedValues = useMemo(() => value || [], [value]);

  const handleChange = (nextValues) => {
    if (onChange) {
      onChange(nextValues);
    }
  };

  const selectedOptions = useMemo(() => {
    if (!selectedValues.length) return [];

    const map = new Map(employeeOptions.map(option => [option.value, option]));
    return selectedValues
      .map(v => map.get(v))
      .filter(Boolean);
  }, [employeeOptions, selectedValues]);

  const handleRemove = (removedValue) => {
    const nextValues = selectedValues.filter(v => v !== removedValue);
    handleChange(nextValues);
  }

  const onInputKeyDown = (e) => {
    if (disableBackspaceRemove && e.key === 'Backspace') {
      e.stopPropagation();
    }
  }

  return (
    <StyledUserMultiSelectWrapper direction='vertical' size='middle' className={className} style={style}>
      <Select
        mode='multiple'
        showSearch
        allowClear={allowClear}
        optionFilterProp={optionFilterProp}
        options={employeeOptions}
        value={selectedValues}
        onChange={handleChange}
        onInputKeyDown={onInputKeyDown}
        placeholder={selectProps?.placeholder || t('selectEmployee')}
        disabled={disabled}
        size={size}
        maxTagCount={0}
        tagRender={() =>
          selectProps?.placeholder ? <StyledUserMultiSelectPlaceholder>{selectProps?.placeholder}</StyledUserMultiSelectPlaceholder> : null
        }
        {...selectProps}
      />

      {showSelectedList && selectedOptions.length > 0 && (
        <StyledUserMultiSelectSelectedList direction='vertical'>
          {selectedOptions.map(option => (
            <StyledUserMultiSelectSelectedItem key={option.value}>
              <StyledUserMultiSelectSelectedItemLabel>
                {option.label || option.optionLabel || option.value}
              </StyledUserMultiSelectSelectedItemLabel>
              <Button
                icon={<CloseOutlined style={{color: colorsStyles.neutral_7}} />}
                onClick={() => handleRemove(option.value)}
                type='text'
                size='small'
              />
            </StyledUserMultiSelectSelectedItem>
          ))}
        </StyledUserMultiSelectSelectedList>
      )}
    </StyledUserMultiSelectWrapper>
  );
};

UserMultiSelect.propTypes = {
  value: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'middle', 'large']),
  valueKey: PropTypes.string,
  optionFilterProp: PropTypes.string,
  allowClear: PropTypes.bool,
  showSelectedList: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  selectProps: PropTypes.object,
};

export default UserMultiSelect;
